import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, Heart, Sparkles, Star, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';

const copy = {
  en: {
    title: 'Share Say It NOW',
    subtitle: 'Help more people say what matters.',
    desc: 'When someone joins using your referral link and purchases a plan, you earn free announcements as a thank you for spreading meaningful conversations.',
    cta: 'Start Sharing',
    note: 'Earn up to 20 free announcements.',
    referral: 'Referral',
    referrals: 'Referrals',
    earn: 'Earn',
    freeAnnouncement: 'free announcement',
    moreAnnouncements: 'more announcements',
  },
  es: {
    title: 'Comparte Say It NOW',
    subtitle: 'Ayuda a más personas a decir lo que importa.',
    desc: 'Cuando alguien se une usando tu enlace de referencia y compra un plan, ganas anuncios gratis como agradecimiento por difundir conversaciones significativas.',
    cta: 'Empezar a Compartir',
    note: 'Gana hasta 20 anuncios gratis.',
    referral: 'Referencia',
    referrals: 'Referencias',
    earn: 'Gana',
    freeAnnouncement: 'anuncio gratis',
    moreAnnouncements: 'anuncios más',
  },
  fr: {
    title: 'Partagez Say It NOW',
    subtitle: 'Aidez plus de gens à dire ce qui compte.',
    desc: 'Quand quelqu\'un rejoint via votre lien de parrainage et achète un plan, vous gagnez des annonces gratuites en remerciement.',
    cta: 'Commencer à Partager',
    note: 'Gagnez jusqu\'à 20 annonces gratuites.',
    referral: 'Parrainage',
    referrals: 'Parrainages',
    earn: 'Gagnez',
    freeAnnouncement: 'annonce gratuite',
    moreAnnouncements: 'annonces en plus',
  },
  pt: {
    title: 'Compartilhe o Say It NOW',
    subtitle: 'Ajude mais pessoas a dizer o que importa.',
    desc: 'Quando alguém se junta usando seu link de indicação e compra um plano, você ganha anúncios grátis como agradecimento.',
    cta: 'Começar a Compartilhar',
    note: 'Ganhe até 20 anúncios grátis.',
    referral: 'Indicação',
    referrals: 'Indicações',
    earn: 'Ganhe',
    freeAnnouncement: 'anúncio grátis',
    moreAnnouncements: 'anúncios a mais',
  },
  ar: {
    title: 'شارك Say It NOW',
    subtitle: 'ساعد المزيد من الناس على قول ما يهم.',
    desc: 'عندما ينضم شخص ما عبر رابط الإحالة الخاص بك ويشتري خطة، تحصل على إعلانات مجانية.',
    cta: 'ابدأ المشاركة',
    note: 'اكسب حتى 20 إعلانًا مجانيًا.',
    referral: 'إحالة',
    referrals: 'إحالات',
    earn: 'اكسب',
    freeAnnouncement: 'إعلان مجاني',
    moreAnnouncements: 'إعلانات إضافية',
  },
  ja: {
    title: 'Say It NOW を共有',
    subtitle: 'もっと多くの人が大切なことを伝えられるように。',
    desc: '紹介リンクで誰かが参加しプランを購入すると、無料アナウンスメントを獲得できます。',
    cta: '共有を始める',
    note: '最大20件の無料アナウンスメントを獲得。',
    referral: '紹介',
    referrals: '紹介',
    earn: '獲得',
    freeAnnouncement: '無料アナウンスメント',
    moreAnnouncements: '追加アナウンスメント',
  },
  ko: {
    title: 'Say It NOW 공유하기',
    subtitle: '더 많은 사람들이 중요한 말을 할 수 있도록 도와주세요.',
    desc: '추천 링크로 누군가가 가입하고 플랜을 구매하면, 무료 어나운스먼트를 받을 수 있습니다.',
    cta: '공유 시작',
    note: '최대 20개의 무료 어나운스먼트를 획득하세요.',
    referral: '추천',
    referrals: '추천',
    earn: '획득',
    freeAnnouncement: '무료 어나운스먼트',
    moreAnnouncements: '추가 어나운스먼트',
  },
  id: {
    title: 'Bagikan Say It NOW',
    subtitle: 'Bantu lebih banyak orang mengatakan yang penting.',
    desc: 'Ketika seseorang bergabung menggunakan tautan referensi Anda dan membeli paket, Anda mendapatkan pengumuman gratis.',
    cta: 'Mulai Berbagi',
    note: 'Dapatkan hingga 20 pengumuman gratis.',
    referral: 'Referensi',
    referrals: 'Referensi',
    earn: 'Dapatkan',
    freeAnnouncement: 'pengumuman gratis',
    moreAnnouncements: 'pengumuman tambahan',
  },

  hi: {
    title: 'Say It NOW शेयर करें',
    subtitle: 'अधिक लोगों को यह कहने में मदद करें कि क्या मायने रखता है।',
    desc: 'जब कोई आपके रेफरल लिंक का उपयोग करके जुड़ता है और एक योजना खरीदता है, तो आप सार्थक बातचीत फैलाने के लिए धन्यवाद के रूप में मुफ्त घोषणाएँ अर्जित करते हैं।',
    cta: 'साझा करना शुरू करें',
    note: '20 मुफ्त घोषणाएँ तक कमाएँ।',
    referral: 'रेफरल',
    referrals: 'रेफरल',
    earn: 'कमाएँ',
    freeAnnouncement: 'मुफ्त घोषणा',
    moreAnnouncements: 'और घोषणाएँ',
  },
};

const tiers = [
  { referrals: 1, reward: 1, isMore: false, icon: Heart },
  { referrals: 5, reward: 3, isMore: true, icon: Gift },
  { referrals: 12, reward: 6, isMore: true, icon: Star },
  { referrals: 20, reward: 12, isMore: true, icon: Sparkles },
];

export function ShareTheWordsSection() {
  const { language } = useLanguage();
  const c = lc(copy, language);

  return (
    <section id="share-the-words" className="py-20 md:py-28 relative overflow-hidden">
      {/* Soft radial glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-5">
            <Users className="h-3.5 w-3.5" />
            Share the Words
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
            {c.title}.<br />
            <span className="text-primary">{c.subtitle}</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg leading-relaxed">
            {c.desc}
          </p>
        </motion.div>

        {/* Tier Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-10">
          {tiers.map((tier, i) => {
            const Icon = tier.icon;
            return (
              <motion.div
                key={tier.referrals}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group relative"
              >
                <div className="relative rounded-2xl border border-border/60 bg-card/80 backdrop-blur-sm p-5 md:p-6 text-center transition-all duration-300 hover:border-primary/40 hover:shadow-elevation-2">
                  {/* Tier number badge */}
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tracking-wider uppercase">
                    Tier {i + 1}
                  </div>

                  <div className="mt-3 mb-3 flex justify-center">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>

                  <p className="text-2xl md:text-3xl font-bold text-foreground mb-1">
                    {tier.referrals}
                  </p>
                  <p className="text-xs text-muted-foreground mb-3">
                    {tier.referrals === 1 ? c.referral : c.referrals}
                  </p>

                  <div className="border-t border-border/40 pt-3">
                    <p className="text-sm font-semibold text-primary">
                      {c.earn} {tier.reward}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {tier.isMore ? c.moreAnnouncements : c.freeAnnouncement}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Note + CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center"
        >
          <p className="text-sm text-muted-foreground mb-6 italic">
            {c.note}
          </p>
          <Link to="/signup">
            <Button size="lg" className="rounded-2xl gap-2 px-8">
              <Users className="h-4 w-4" />
              {c.cta}
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
